import { useQuery } from "@tanstack/react-query";
import type { BiQuestionLogDto } from "@bi/types";
import { api } from "../lib/api";
import { fmtDateTime } from "../lib/format";

const STATUS_BADGE: Record<BiQuestionLogDto["status"], string> = {
  answered:            "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed:              "bg-rose-50 text-rose-700 border-rose-200",
  pending:             "bg-amber-50 text-amber-700 border-amber-200",
  interpreted:         "bg-amber-50 text-amber-700 border-amber-200",
  needs_clarification: "bg-sistolica-50 text-sistolica-700 border-sistolica-200",
};

export function LogsPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["bi", "questions"],
    queryFn: () => api.get<{ items: BiQuestionLogDto[] }>("/bi/questions"),
  });

  if (isLoading) return <p className="text-sistolica-500">Carregando…</p>;
  if (error)     return <p className="text-rose-700">{(error as Error).message}</p>;

  const items = data?.items ?? [];

  return (
    <div className="space-y-6">
      <header>
        <div className="text-xs uppercase tracking-widest text-sistolica-500">Auditoria</div>
        <h1 className="text-2xl font-semibold text-sistolica-800">Perguntas e respostas</h1>
        <p className="mt-1 text-sm text-sistolica-500">
          Toda interação por web ou WhatsApp é registrada aqui: pergunta original,
          intenção interpretada, métrica usada e tempo de resposta.
        </p>
      </header>

      <div className="bg-white border border-sistolica-100 rounded-xl shadow-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sistolica-50 text-sistolica-600">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Quando</th>
              <th className="text-left px-4 py-3 font-medium">Usuário</th>
              <th className="text-left px-4 py-3 font-medium">Canal</th>
              <th className="text-left px-4 py-3 font-medium">Pergunta</th>
              <th className="text-left px-4 py-3 font-medium">Métrica</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-right px-4 py-3 font-medium">ms</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sistolica-100">
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sistolica-400">Nenhuma pergunta registrada ainda.</td></tr>
            )}
            {items.map((q) => (
              <tr key={q.id} className="hover:bg-sistolica-50/50">
                <td className="px-4 py-3 text-sistolica-600">{fmtDateTime(q.createdAt)}</td>
                <td className="px-4 py-3 text-sistolica-700">{q.userName ?? "—"}</td>
                <td className="px-4 py-3 text-sistolica-600 capitalize">{q.channel}</td>
                <td className="px-4 py-3 text-sistolica-800 max-w-md truncate" title={q.question}>{q.question}</td>
                <td className="px-4 py-3 text-sistolica-600 font-mono text-xs">{q.metricKey ?? "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full border ${STATUS_BADGE[q.status]}`}>
                    {q.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-sistolica-500 font-mono text-xs">
                  {q.durationMs ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
